import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, Plus, Edit, Trash2, Save, X, Package, Mail, Copy, Check, Download, Eye, Warehouse, ArrowUpDown, Minus, Moon, Sun, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import Inventario from './Inventario';
import { subscribeWebPush, registerPushServiceWorker, sendTestPush } from './web-push-client';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const ANO_ATUAL = new Date().getFullYear();
const SENHA_EDITAR = 'APAGAR';

function itemVazio() {
  return {
    descricao_equipamento: '',
    marca_equipamento: '',
    modelo_equipamento: '',
    defeito_apresentado: '',
    unidade: 'PC',
    quantidade: 1,
    valor_unitario: '',
    valor_total: '',
    informacoes_complementares: '',
    foto: '',
    foto_preview: '',
  };
}

function comprimirFoto(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Selecione uma imagem (JPG, PNG ou foto da câmera).'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Não foi possível ler a foto.'));
    reader.onload = () => {
      const img = new window.Image();
      img.onerror = () => reject(new Error('Imagem inválida.'));
      img.onload = () => {
        const maxLado = 1280;
        let { width, height } = img;
        if (width > maxLado || height > maxLado) {
          const escala = maxLado / Math.max(width, height);
          width = Math.round(width * escala);
          height = Math.round(height * escala);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
        if (dataUrl.length > 700000) {
          const menor = canvas.toDataURL('image/jpeg', 0.5);
          resolve(menor);
          return;
        }
        resolve(dataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function pedirSenha(acao) {
  const senha = window.prompt(`Digite a senha para ${acao}:`);
  if (senha === null) return false;
  if (senha !== SENHA_EDITAR) {
    alert('Senha incorreta.');
    return false;
  }
  return true;
}

function formatDateBR(dateString) {
  if (!dateString) return '';
  const [y, m, d] = String(dateString).slice(0, 10).split('-');
  if (!y || !m || !d) return dateString;
  return `${d}/${m}/${y}`;
}

function diasEmConserto(equipamento) {
  const dateString = equipamento?.data_solicitacao;
  if (!dateString) return null;
  const inicio = new Date(`${String(dateString).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(inicio.getTime())) return null;
  const fim = equipamento?.chegou && equipamento?.data_chegada
    ? new Date(`${String(equipamento.data_chegada).slice(0, 10)}T12:00:00`)
    : new Date();
  fim.setHours(12, 0, 0, 0);
  if (Number.isNaN(fim.getTime())) return null;
  const diff = Math.floor((fim - inicio) / (1000 * 60 * 60 * 24));
  return diff < 0 ? 0 : diff;
}

// Controle do aviso na tela (independente da permissão do Windows)
// v6: não abre sozinho; só pelo botão "Avisos / notificações"
const NOTIF_ACK_KEY = 'controle_notif_ack_v6';
const NOTIF_SNOOZE_KEY = 'controle_notif_snooze_v6';
const CHEGADA_POPUP_KEY = 'controle_chegada_popup_v1';
const CHEGADAS_VISTAS_KEY = 'controle_chegadas_vistas_v1';
const THEME_KEY = 'controle_theme';

function getInitialTheme() {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch (_) {
    /* ignore */
  }
}

function chegadaEventKey(eq) {
  if (!eq?.id) return '';
  return `${eq.id}|${eq.data_atualizacao || ''}|${eq.data_chegada || ''}`;
}

function loadChegadasVistas() {
  try {
    const raw = localStorage.getItem(CHEGADAS_VISTAS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (_) {
    return new Set();
  }
}

function saveChegadasVistas(set) {
  try {
    // Mantém só as 200 mais recentes
    const arr = [...set].slice(-200);
    localStorage.setItem(CHEGADAS_VISTAS_KEY, JSON.stringify(arr));
  } catch (_) {
    /* ignore */
  }
}

function marcarChegadaVista(key) {
  if (!key) return;
  const set = loadChegadasVistas();
  set.add(key);
  saveChegadasVistas(set);
}

function jaViuChegada(key) {
  if (!key) return true;
  return loadChegadasVistas().has(key);
}

function payloadFromEquipamento(eq) {
  return {
    id: eq.id,
    key: chegadaEventKey(eq),
    codigo: eq.codigo_processo || 'Equipamento',
    descricao: eq.descricao_equipamento || '',
    data: formatDateBR(eq.data_chegada || new Date().toISOString().slice(0, 10)),
  };
}

async function notificarChegadaEquipamento(equipamento) {
  const codigo = equipamento.codigo_processo || 'Equipamento';
  const desc = equipamento.descricao_equipamento || '';
  const hoje = new Date().toISOString().slice(0, 10);
  const titulo = 'Equipamento chegou';
  const corpo = `${codigo}${desc ? ` — ${desc}` : ''}\nChegou em ${formatDateBR(hoje)}`;

  if (typeof window === 'undefined' || !('Notification' in window)) {
    return { ok: false, reason: 'unsupported' };
  }

  try {
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      return { ok: false, reason: permission };
    }

    const iconUrl = `${window.location.origin}/logo.png`;
    const n = new Notification(titulo, {
      body: corpo,
      tag: `chegada-${equipamento.id}-${Date.now()}`,
      icon: iconUrl,
      requireInteraction: true,
      silent: false,
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch (_) {
        /* ignore */
      }
      n.close();
    };
    return { ok: true };
  } catch (err) {
    console.error('Notificação do navegador:', err);
    return { ok: false, reason: 'error' };
  }
}

function getNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  try {
    return Notification.permission || 'default';
  } catch (_) {
    return 'unsupported';
  }
}

function getNotifSnoozeUntil() {
  try {
    const raw = localStorage.getItem(NOTIF_SNOOZE_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch (_) {
    return 0;
  }
}

function setNotifSnoozeOneDay() {
  const until = Date.now() + 24 * 60 * 60 * 1000;
  try {
    localStorage.setItem(NOTIF_SNOOZE_KEY, String(until));
  } catch (_) {
    /* ignore */
  }
  return until;
}

function clearNotifSnooze() {
  try {
    localStorage.removeItem(NOTIF_SNOOZE_KEY);
  } catch (_) {
    /* ignore */
  }
}

function setNotifAcknowledged() {
  try {
    localStorage.setItem(NOTIF_ACK_KEY, '1');
  } catch (_) {
    /* ignore */
  }
}

function isNotifAcknowledged() {
  try {
    return localStorage.getItem(NOTIF_ACK_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/** Popup de instrução: não abre sozinho ao carregar a página. */
function shouldShowNotifPopup() {
  return false;
}

function saveChegadaPopup(payload) {
  try {
    sessionStorage.setItem(CHEGADA_POPUP_KEY, JSON.stringify(payload));
  } catch (_) {
    /* ignore */
  }
}

function loadChegadaPopup() {
  try {
    const raw = sessionStorage.getItem(CHEGADA_POPUP_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function clearChegadaPopupStorage() {
  try {
    sessionStorage.removeItem(CHEGADA_POPUP_KEY);
  } catch (_) {
    /* ignore */
  }
}

function getAnoEquipamento(eq) {
  if (eq?.data_solicitacao) {
    const y = parseInt(String(eq.data_solicitacao).slice(0, 4), 10);
    if (Number.isFinite(y)) return y;
  }
  const m = String(eq?.codigo_processo || '').match(/\/(\d{4})$/);
  return m ? parseInt(m[1], 10) : null;
}

function App() {
  const [modulo, setModulo] = useState('conserto');
  const [equipamentos, setEquipamentos] = useState([]);
  const [setores, setSetores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anoSelecionado, setAnoSelecionado] = useState(ANO_ATUAL);
  const [setorFiltro, setSetorFiltro] = useState('');
  const [ordenacao, setOrdenacao] = useState('data_desc');
  const [listaChegada, setListaChegada] = useState('em_conserto'); // em_conserto | chegaram
  const [emailText, setEmailText] = useState('');
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);
  const [chegadaPopup, setChegadaPopup] = useState(() => loadChegadaPopup()); // { codigo, descricao, data, key? }
  const [notifPermission, setNotifPermission] = useState(() => getNotificationPermission());
  const [showNotifPopup, setShowNotifPopup] = useState(() => shouldShowNotifPopup());
  const [theme, setTheme] = useState(() => getInitialTheme());
  const [pushStatus, setPushStatus] = useState('');
  const [fotoPopup, setFotoPopup] = useState(null);
  const chegadaPopupRef = useRef(chegadaPopup);
  const chegadaQueueRef = useRef([]);
  const chegadasPrimedRef = useRef(false);
  const [formData, setFormData] = useState({
    codigo_processo: '',
    data_solicitacao: '',
    descricao_equipamento: '',
    marca_equipamento: '',
    modelo_equipamento: '',
    defeito_apresentado: '',
    setor_origem: '',
    codigo_setor: '',
    unidade: 'PC',
    quantidade: 1,
    valor_unitario: '',
    valor_total: '',
    informacoes_complementares: ''
  });
  const [formItens, setFormItens] = useState([itemVazio()]);

  useEffect(() => {
    fetchEquipamentos();
    fetchSetores();
  }, []);

  useEffect(() => {
    chegadaPopupRef.current = chegadaPopup;
  }, [chegadaPopup]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    setNotifPermission(getNotificationPermission());
    setShowNotifPopup(shouldShowNotifPopup());
    registerPushServiceWorker().catch(() => {});
    if (getNotificationPermission() === 'granted') {
      subscribeWebPush(API_URL)
        .then((r) => {
          if (r?.ok) setPushStatus(`Push OK (${r.subscriptions ?? '?'} PC inscrito)`);
          else setPushStatus(`Push: ${r?.reason || 'falhou'}`);
        })
        .catch((err) => setPushStatus(`Push: ${err.message || 'erro'}`));
    }
  }, []);

  // Polling: avisa os OUTROS PCs quando alguém marcar "chegou"
  useEffect(() => {
    let cancelled = false;

    const mostrarChegadaRemota = (eq) => {
      const payload = payloadFromEquipamento(eq);
      if (chegadaPopupRef.current) {
        chegadaQueueRef.current.push({ payload, eq });
      } else {
        setChegadaPopup(payload);
        saveChegadaPopup(payload);
        setToast({
          type: 'success',
          message: `${payload.codigo} — chegou (outro usuário)`,
        });
        window.setTimeout(() => setToast(null), 8000);
      }
      notificarChegadaEquipamento(eq).catch(() => {});
    };

    const pollChegadas = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/chegadas-recentes`, {
          params: { minutos: 180 },
        });
        if (cancelled) return;
        const lista = Array.isArray(data?.chegadas) ? data.chegadas : [];

        if (!chegadasPrimedRef.current) {
          // Ao abrir o app: marca as já existentes como vistas (não flooda popup antigo)
          lista.forEach((eq) => marcarChegadaVista(chegadaEventKey(eq)));
          chegadasPrimedRef.current = true;
          return;
        }

        let temNova = false;
        for (const eq of lista) {
          const key = chegadaEventKey(eq);
          if (jaViuChegada(key)) continue;
          marcarChegadaVista(key);
          temNova = true;
          mostrarChegadaRemota(eq);
        }
        if (temNova) {
          fetchEquipamentos();
        }
      } catch (err) {
        console.error('Erro ao buscar chegadas recentes:', err);
      }
    };

    pollChegadas();
    const timer = window.setInterval(pollChegadas, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const handleLiberarNotificacoes = async () => {
    if (!('Notification' in window)) {
      setNotifPermission('unsupported');
      setNotifAcknowledged();
      clearNotifSnooze();
      setShowNotifPopup(false);
      alert('Este navegador não envia notificação do Windows. Use Chrome ou Edge. O aviso CHEGOU na tela continua funcionando.');
      return;
    }
    const atual = Notification.permission;
    if (atual === 'granted') {
      setNotifPermission('granted');
      setNotifAcknowledged();
      clearNotifSnooze();
      setShowNotifPopup(false);
      try {
        const r = await subscribeWebPush(API_URL);
        setPushStatus(
          r?.ok
            ? `Push ativado (${r.subscriptions ?? 1} PC)`
            : `Push: ${r?.reason || 'falhou'}`
        );
        if (!r?.ok) {
          alert(`Notificação liberada, mas o push falhou: ${r?.reason || 'erro'}. Tente de novo no botão amarelo.`);
        }
      } catch (err) {
        setPushStatus(`Push: ${err.message || 'erro'}`);
        alert(`Falha ao ativar push: ${err.message || err}`);
      }
      try {
        new Notification('Notificações OK', {
          body: 'Mesmo com o app fechado, o Windows avisará quando um equipamento chegar.',
          tag: `notif-ok-${Date.now()}`,
          requireInteraction: true,
        });
      } catch (_) {
        /* ignore */
      }
      return;
    }
    if (atual === 'denied') {
      setNotifPermission('denied');
      setShowNotifPopup(true);
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setNotifPermission(result);
      if (result === 'granted') {
        setNotifAcknowledged();
        clearNotifSnooze();
        setShowNotifPopup(false);
        try {
          const r = await subscribeWebPush(API_URL);
          setPushStatus(
            r?.ok
              ? `Push ativado (${r.subscriptions ?? 1} PC)`
              : `Push: ${r?.reason || 'falhou'}`
          );
        } catch (err) {
          setPushStatus(`Push: ${err.message || 'erro'}`);
          alert(`Falha ao ativar push: ${err.message || err}`);
        }
        try {
          new Notification('Notificações ativadas', {
            body: 'Mesmo com o app fechado, o Windows avisará quando um equipamento chegar.',
            tag: `notif-ok-${Date.now()}`,
            icon: `${window.location.origin}/logo.png`,
            requireInteraction: true,
          });
        } catch (_) {
          /* ignore */
        }
      } else {
        setShowNotifPopup(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleVerificarNotificacoes = async () => {
    const atual = getNotificationPermission();
    setNotifPermission(atual);
    if (atual === 'granted') {
      setNotifAcknowledged();
      clearNotifSnooze();
      setShowNotifPopup(false);
      try {
        const r = await subscribeWebPush(API_URL);
        setPushStatus(
          r?.ok
            ? `Push ativado (${r.subscriptions ?? 1} PC)`
            : `Push: ${r?.reason || 'falhou'}`
        );
      } catch (err) {
        setPushStatus(`Push: ${err.message || 'erro'}`);
        alert(`Falha ao ativar push: ${err.message || err}`);
      }
      setToast({ type: 'success', message: 'Notificações liberadas (funcionam com o app fechado)!' });
      window.setTimeout(() => setToast(null), 3000);
    } else {
      setShowNotifPopup(true);
      alert('Ainda não está liberado. No Windows: Chrome/Edge → cadeado ao lado do endereço → Notificações → Permitir.');
    }
  };

  const handleTestarPush = async () => {
    try {
      if (Notification.permission !== 'granted') {
        alert('Libere as notificações primeiro.');
        return;
      }
      await subscribeWebPush(API_URL);
      const result = await sendTestPush(API_URL);
      if (result.total === 0) {
        alert('Nenhum PC inscrito ainda. Clique em “Permitir notificações” em cada computador.');
        return;
      }
      alert(`Teste enviado: ${result.sent} ok, ${result.failed} falha (de ${result.total} PC).`);
    } catch (err) {
      alert(`Erro no teste: ${err.message || err}`);
    }
  };

  const handleToggleTheme = () => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  };

  const handleFecharNotifPopup = () => {
    setNotifAcknowledged();
    clearNotifSnooze();
    setShowNotifPopup(false);
  };

  const handleEntendiNotif = () => {
    setNotifAcknowledged();
    clearNotifSnooze();
    setShowNotifPopup(false);
  };

  const handleFecharChegadaPopup = () => {
    clearChegadaPopupStorage();
    const next = chegadaQueueRef.current.shift();
    if (next) {
      setChegadaPopup(next.payload);
      saveChegadaPopup(next.payload);
      notificarChegadaEquipamento(next.eq).catch(() => {});
      return;
    }
    setChegadaPopup(null);
    if (shouldShowNotifPopup()) {
      setShowNotifPopup(true);
    }
  };

  const fetchEquipamentos = async () => {
    try {
      const response = await axios.get(`${API_URL}/equipamentos`);
      setEquipamentos(response.data);
    } catch (error) {
      console.error('Erro ao buscar equipamentos:', error);
    }
  };

  const fetchSetores = async () => {
    try {
      const response = await axios.get(`${API_URL}/setores`);
      setSetores(response.data);
    } catch (error) {
      console.error('Erro ao buscar setores:', error);
    }
  };

  const fetchProximoCodigo = async () => {
    try {
      const response = await axios.get(`${API_URL}/proximo-codigo`);
      setFormData(prev => ({ ...prev, codigo_processo: response.data.proximo_codigo }));
    } catch (error) {
      console.error('Erro ao buscar próximo código:', error);
    }
  };

  const anosDisponiveis = (() => {
    const anos = new Set(
      equipamentos.map(getAnoEquipamento).filter((y) => Number.isFinite(y))
    );
    anos.add(ANO_ATUAL);
    return [...anos].sort((a, b) => b - a);
  })();

  const equipamentosDoAno = equipamentos.filter(
    (eq) => getAnoEquipamento(eq) === anoSelecionado
  );

  const setoresFiltro = [
    ...new Set(
      equipamentosDoAno
        .map((eq) => (eq.setor_origem || '').trim())
        .filter(Boolean)
    ),
  ].sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const equipamentosFiltrados = equipamentosDoAno.filter((eq) => {
    if (setorFiltro && eq.setor_origem !== setorFiltro) return false;
    const termo = searchTerm.trim().toLowerCase();
    if (!termo) return true;
    return [
      eq.codigo_processo,
      eq.descricao_equipamento,
      eq.marca_equipamento,
      eq.modelo_equipamento,
      eq.setor_origem,
      eq.defeito_apresentado,
    ].some((campo) => String(campo || '').toLowerCase().includes(termo));
  });

  const ordenarLista = (lista) =>
    [...lista].sort((a, b) => {
      switch (ordenacao) {
        case 'dias_asc':
          return (diasEmConserto(a) ?? 99999) - (diasEmConserto(b) ?? 99999);
        case 'data_desc':
          return String(b.data_solicitacao || '').localeCompare(String(a.data_solicitacao || ''));
        case 'data_asc':
          return String(a.data_solicitacao || '').localeCompare(String(b.data_solicitacao || ''));
        case 'codigo':
          return String(a.codigo_processo || '').localeCompare(String(b.codigo_processo || ''), 'pt-BR');
        case 'setor':
          return String(a.setor_origem || '').localeCompare(String(b.setor_origem || ''), 'pt-BR');
        case 'chegada_desc':
          return String(b.data_chegada || '').localeCompare(String(a.data_chegada || ''));
        case 'dias_desc':
        default:
          return (diasEmConserto(b) ?? -1) - (diasEmConserto(a) ?? -1);
      }
    });

  const equipamentosPendentes = ordenarLista(
    equipamentosFiltrados.filter((eq) => !eq.chegou)
  );
  const equipamentosChegados = ordenarLista(
    equipamentosFiltrados.filter((eq) => eq.chegou)
  );
  const listaAtual =
    listaChegada === 'chegaram' ? equipamentosChegados : equipamentosPendentes;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const valorUnitario = parseFloat(formData.valor_unitario) || 0;
        const quantidade = parseInt(formData.quantidade, 10) || 0;
        const valorTotal = valorUnitario * quantidade;
        await axios.put(`${API_URL}/equipamentos/${editingId}`, {
          ...formData,
          valor_unitario: valorUnitario,
          valor_total: valorTotal,
          quantidade,
          foto: formData.foto || undefined,
        });
      } else {
        if (!formData.setor_origem) {
          alert('Selecione o setor de origem.');
          return;
        }
        if (!formItens.length) {
          alert('Adicione ao menos um item.');
          return;
        }
        for (let i = 0; i < formItens.length; i++) {
          const it = formItens[i];
          if (!String(it.descricao_equipamento || '').trim()) {
            alert(`Item ${i + 1}: preencha a descrição.`);
            return;
          }
          if (!String(it.defeito_apresentado || '').trim()) {
            alert(`Item ${i + 1}: preencha o defeito/ação.`);
            return;
          }
        }
        const { data } = await axios.post(`${API_URL}/equipamentos/lote`, {
          data_solicitacao: formData.data_solicitacao,
          setor_origem: formData.setor_origem,
          codigo_setor: formData.codigo_setor,
          itens: formItens.map((it) => ({
            descricao_equipamento: it.descricao_equipamento,
            marca_equipamento: it.marca_equipamento,
            modelo_equipamento: it.modelo_equipamento,
            defeito_apresentado: it.defeito_apresentado,
            unidade: it.unidade,
            informacoes_complementares: it.informacoes_complementares,
            quantidade: parseInt(it.quantidade, 10) || 1,
            valor_unitario:
              it.valor_unitario === '' || it.valor_unitario == null
                ? null
                : parseFloat(it.valor_unitario),
            foto: it.foto || undefined,
          })),
        });
        const total = data?.total || formItens.length;
        const codigos = (data?.criados || []).map((c) => c.codigo_processo).join(', ');
        setToast({
          type: 'success',
          message:
            total > 1
              ? `${total} processos criados: ${codigos}`
              : `Processo criado: ${codigos || 'OK'}`,
        });
        window.setTimeout(() => setToast(null), 6000);
      }

      fetchEquipamentos();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar equipamento:', error);
      const msg =
        error?.response?.data?.error ||
        'Erro ao salvar. Verifique os dados ou se o código já existe.';
      alert(msg);
    }
  };

  const handleEdit = (equipamento) => {
    if (!pedirSenha('editar')) return;
    setEditingId(equipamento.id);
    setFormItens([itemVazio()]);
    setFormData({
      codigo_processo: equipamento.codigo_processo,
      data_solicitacao: equipamento.data_solicitacao,
      descricao_equipamento: equipamento.descricao_equipamento,
      marca_equipamento: equipamento.marca_equipamento || '',
      modelo_equipamento: equipamento.modelo_equipamento || '',
      defeito_apresentado: equipamento.defeito_apresentado,
      setor_origem: equipamento.setor_origem,
      codigo_setor: equipamento.codigo_setor || '',
      unidade: equipamento.unidade,
      quantidade: equipamento.quantidade,
      valor_unitario: equipamento.valor_unitario || '',
      valor_total: equipamento.valor_total || '',
      informacoes_complementares: equipamento.informacoes_complementares || '',
      foto: '',
      foto_preview: equipamento.tem_foto ? `${API_URL}/equipamentos/${equipamento.id}/foto?t=${Date.now()}` : '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!pedirSenha('apagar')) return;
    if (window.confirm('Tem certeza que deseja deletar este equipamento?')) {
      try {
        await axios.delete(`${API_URL}/equipamentos/${id}`);
        fetchEquipamentos();
      } catch (error) {
        console.error('Erro ao deletar equipamento:', error);
      }
    }
  };

  const handleToggleChegada = async (equipamento) => {
    const chegou = !equipamento.chegou;
    // Desmarcar exige senha; marcar só registra e encerra o processo
    if (!chegou && !pedirSenha('reabrir processo')) return;

    // Mostra o popup ANTES da rede — assim o React consegue desenhar
    if (chegou) {
      const payload = {
        id: equipamento.id,
        key: `local-${equipamento.id}-${Date.now()}`,
        codigo: equipamento.codigo_processo || 'Equipamento',
        descricao: equipamento.descricao_equipamento || '',
        data: formatDateBR(new Date().toISOString().slice(0, 10)),
      };
      setChegadaPopup(payload);
      saveChegadaPopup(payload);
      setToast({
        type: 'success',
        message: `${payload.codigo} — chegou`,
      });
      window.setTimeout(() => setToast(null), 8000);
    }

    try {
      const { data } = await axios.patch(
        `${API_URL}/equipamentos/${equipamento.id}/chegada`,
        { chegou }
      );
      await fetchEquipamentos();
      if (chegou) {
        // Marca como vista neste PC para o polling não repetir o mesmo aviso
        marcarChegadaVista(chegadaEventKey(data || equipamento));
        const dataChegada = data?.data_chegada || new Date().toISOString().slice(0, 10);
        const dataBr = formatDateBR(dataChegada);
        const payload = {
          id: equipamento.id,
          key: chegadaEventKey(data || equipamento),
          codigo: equipamento.codigo_processo || 'Equipamento',
          descricao: equipamento.descricao_equipamento || '',
          data: dataBr,
        };
        setChegadaPopup(payload);
        saveChegadaPopup(payload);
        notificarChegadaEquipamento(equipamento).catch(() => {});
        setListaChegada('chegaram');
      } else {
        setListaChegada('em_conserto');
      }
    } catch (error) {
      console.error('Erro ao atualizar chegada:', error);
      if (chegou) {
        setChegadaPopup(null);
        clearChegadaPopupStorage();
      }
      alert('Erro ao marcar chegada do equipamento.');
    }
  };

  const handleOpenModal = async () => {
    const hoje = new Date().toISOString().split('T')[0];
    setEditingId(null);
    setFormItens([itemVazio()]);
    setFormData({
      codigo_processo: '',
      data_solicitacao: hoje,
      descricao_equipamento: '',
      marca_equipamento: '',
      modelo_equipamento: '',
      defeito_apresentado: '',
      setor_origem: '',
      codigo_setor: '',
      unidade: 'PC',
      quantidade: 1,
      valor_unitario: '',
      valor_total: '',
      informacoes_complementares: ''
    });
    await fetchProximoCodigo();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormItens([itemVazio()]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Se selecionou um setor, atualizar também o código do setor
      if (name === 'setor_origem') {
        const setorSelecionado = setores.find(s => s.nome === value);
        if (setorSelecionado) {
          newData.codigo_setor = setorSelecionado.codigo;
        }
      }
      
      if (name === 'valor_unitario' || name === 'quantidade') {
        const valorUnitario = parseFloat(newData.valor_unitario) || 0;
        const quantidade = parseInt(newData.quantidade) || 0;
        newData.valor_total = (valorUnitario * quantidade).toString();
      }
      
      return newData;
    });
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    setFormItens((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, [name]: value };
        if (name === 'valor_unitario' || name === 'quantidade') {
          const valorUnitario = parseFloat(next.valor_unitario) || 0;
          const quantidade = parseInt(next.quantidade, 10) || 0;
          next.valor_total = (valorUnitario * quantidade).toString();
        }
        return next;
      })
    );
  };

  const handleAddItem = () => {
    setFormItens((prev) => [...prev, itemVazio()]);
  };

  const handleRemoveItem = (index) => {
    setFormItens((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleItemPhoto = async (index, file) => {
    if (!file) return;
    try {
      const dataUrl = await comprimirFoto(file);
      setFormItens((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, foto: dataUrl, foto_preview: dataUrl } : item
        )
      );
    } catch (err) {
      alert(err.message || 'Erro ao ler a foto.');
    }
  };

  const handleRemoveItemPhoto = (index) => {
    setFormItens((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, foto: '', foto_preview: '' } : item
      )
    );
  };

  const handleEditPhoto = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await comprimirFoto(file);
      setFormData((prev) => ({ ...prev, foto: dataUrl, foto_preview: dataUrl }));
    } catch (err) {
      alert(err.message || 'Erro ao ler a foto.');
    }
  };

  const handleRemoveEditPhoto = () => {
    setFormData((prev) => ({ ...prev, foto: '', foto_preview: '' }));
  };

  const handleGerarEmail = (id) => {
    const url = `${API_URL}/gerar-email/${id}/eml`;
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) {
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const handleVerEmail = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/gerar-email/${id}`);
      setEmailText(response.data.texto_email);
      setShowEmailModal(true);
      setCopied(false);
    } catch (error) {
      console.error('Erro ao gerar email:', error);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportarExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/exportar-excel`, {
        responseType: 'blob'
      });
      
      // Criar URL para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `equipamentos_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar dados');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatDiasConserto = (dias) => {
    if (dias == null) return '-';
    if (dias === 0) return 'Hoje';
    if (dias === 1) return '1 dia';
    return `${dias} dias`;
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-900 flex flex-col">
      {typeof document !== 'undefined' &&
        chegadaPopup &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 2147483646, background: 'rgba(0,0,0,0.75)' }}
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 text-center border-4 border-green-500">
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
                <Check size={36} />
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">Chegou</h2>
              <p className="text-lg font-semibold text-gray-900 mb-1">{chegadaPopup.codigo}</p>
              {chegadaPopup.descricao ? (
                <p className="text-sm text-gray-600 mb-2">{chegadaPopup.descricao}</p>
              ) : null}
              <p className="text-sm text-gray-500 mb-6">Data: {chegadaPopup.data}</p>
              <button
                type="button"
                onClick={handleFecharChegadaPopup}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
      {typeof document !== 'undefined' &&
        showNotifPopup &&
        !chegadaPopup &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 2147483645, background: 'rgba(0,0,0,0.75)' }}
          >
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                <Package size={28} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Avisos de chegada</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Libere as notificações em <strong>cada um dos 3 PCs</strong> (Chrome ou Edge).
                Assim, quando alguém marcar <strong>chegou</strong>, o Windows avisa
                <strong> mesmo com o app fechado</strong>.
              </p>
              {pushStatus ? (
                <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">{pushStatus}</p>
              ) : null}
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
                Este aviso não abre sozinho. Para ver de novo, use o botão amarelo no topo.
              </p>
              {notifPermission === 'unsupported' ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-left">
                  Este navegador não envia notificação do Windows. Use Chrome ou Edge.
                  O aviso na página continua funcionando.
                </p>
              ) : null}
              {notifPermission === 'denied' ? (
                <>
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-left">
                    Notificações do Windows estão <strong>bloqueadas</strong>.
                    Chrome/Edge → cadeado ao lado do endereço → Notificações → Permitir.
                    Depois clique em verificar. Desative o Assistente de Foco se estiver ligado.
                  </p>
                  <button
                    type="button"
                    onClick={handleVerificarNotificacoes}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 mb-2"
                  >
                    Já liberei — verificar
                  </button>
                </>
              ) : notifPermission === 'granted' ? (
                <>
                  <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4 text-left">
                    O navegador já permite notificações. Se o Windows não mostrar o aviso,
                    desative o <strong>Assistente de Foco</strong>. O aviso na tela sempre aparece.
                  </p>
                  <button
                    type="button"
                    onClick={handleEntendiNotif}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 mb-2"
                  >
                    Entendi
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleLiberarNotificacoes}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 mb-2"
                >
                  Permitir notificações do Windows
                </button>
              )}
              <button
                type="button"
                onClick={handleTestarPush}
                className="w-full px-4 py-2 mb-2 text-sm font-semibold border border-blue-400 text-blue-700 dark:text-blue-300 dark:border-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-slate-700"
              >
                Enviar notificação de teste
              </button>
              <button
                type="button"
                onClick={handleFecharNotifPopup}
                className="w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Fechar
              </button>
            </div>
          </div>,
          document.body
        )}
      {toast && (
        <div className="fixed top-4 right-4 z-[99990] max-w-sm">
          <div className="bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-start gap-3">
            <Check size={22} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">Chegou</p>
              <p className="text-sm text-green-50 mt-0.5">{toast.message}</p>
            </div>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ml-2 p-0.5 hover:bg-green-700 rounded"
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
      <div className="bg-blue-600 dark:bg-slate-800 text-white px-4 md:px-6 py-4 flex flex-col gap-3 shrink-0 border-b border-blue-700 dark:border-slate-700">
        <div className="flex items-center gap-4 justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" className="h-12 w-auto" onError={(e) => e.target.style.display = 'none'} />
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2 truncate">
              <Package size={28} className="shrink-0" />
              Lorenzetti Louças — Manutenção
            </h1>
          </div>
          <button
            type="button"
            onClick={handleToggleTheme}
            className="shrink-0 p-2 rounded-lg bg-white/15 hover:bg-white/25"
            title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
            aria-label="Alternar tema"
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            type="button"
            onClick={() => setModulo('conserto')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              modulo === 'conserto' ? 'bg-white text-blue-700' : 'bg-blue-500/40 text-white hover:bg-blue-500/60'
            }`}
          >
            Conserto de equipamentos
          </button>
          <button
            type="button"
            onClick={() => setModulo('inventario')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
              modulo === 'inventario' ? 'bg-white text-blue-700' : 'bg-blue-500/40 text-white hover:bg-blue-500/60'
            }`}
          >
            <Warehouse size={16} />
            Inventário de peças
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                localStorage.removeItem(NOTIF_ACK_KEY);
                localStorage.removeItem(NOTIF_SNOOZE_KEY);
              } catch (_) {
                /* ignore */
              }
              setNotifPermission(getNotificationPermission());
              setShowNotifPopup(true);
            }}
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-amber-400 text-amber-950 hover:bg-amber-300"
          >
            Avisos / notificações
          </button>
          <button
            type="button"
            onClick={handleTestarPush}
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-white/20 text-white hover:bg-white/30"
          >
            Testar push
          </button>
          {pushStatus ? (
            <span className="text-[11px] text-blue-100 max-w-[220px] truncate" title={pushStatus}>
              {pushStatus}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex-1 flex flex-col px-3 md:px-4 py-4 min-h-0">
        <div className="bg-white dark:bg-slate-800 dark:text-gray-100 rounded-lg shadow-lg flex-1 flex flex-col min-h-0 overflow-hidden">
          {modulo === 'inventario' ? (
            <Inventario />
          ) : (
          <div className="p-4 md:p-6 flex flex-col flex-1 min-h-0">
            <div className="flex gap-1 mb-4 border-b border-gray-200 overflow-x-auto shrink-0">
              {anosDisponiveis.map((ano) => (
                <button
                  key={ano}
                  type="button"
                  onClick={() => {
                    setAnoSelecionado(ano);
                    setSetorFiltro('');
                  }}
                  className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                    anoSelecionado === ano
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {ano}
                </button>
              ))}
            </div>

            <div className="flex gap-2 mb-4 shrink-0">
              <button
                type="button"
                onClick={() => setListaChegada('em_conserto')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  listaChegada === 'em_conserto'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Em conserto ({equipamentosPendentes.length})
              </button>
              <button
                type="button"
                onClick={() => setListaChegada('chegaram')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  listaChegada === 'chegaram'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Já chegaram ({equipamentosChegados.length})
              </button>
            </div>

            <div className="flex flex-col lg:flex-row gap-3 mb-4 shrink-0">
              <div className="flex-1 relative min-w-0">
                <input
                  type="text"
                  placeholder={`Buscar em ${anoSelecionado}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
              </div>
              <select
                value={setorFiltro}
                onChange={(e) => setSetorFiltro(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[200px]"
              >
                <option value="">Todos os setores</option>
                {setoresFiltro.map((setor) => (
                  <option key={setor} value={setor}>
                    {setor}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2 min-w-[220px]">
                <ArrowUpDown size={18} className="text-gray-500 shrink-0" />
                <select
                  value={ordenacao}
                  onChange={(e) => setOrdenacao(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  title="Ordenar lista"
                >
                  <option value="data_desc">Data mais recente</option>
                  <option value="data_asc">Data mais antiga</option>
                  <option value="dias_desc">Mais dias em conserto</option>
                  <option value="dias_asc">Menos dias em conserto</option>
                  {listaChegada === 'chegaram' && (
                    <option value="chegada_desc">Chegada mais recente</option>
                  )}
                  <option value="codigo">Código</option>
                  <option value="setor">Setor</option>
                </select>
              </div>
              <button
                type="button"
                onClick={handleOpenModal}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Plus size={20} />
                Novo Processo
              </button>
              <button
                onClick={handleExportarExcel}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Download size={20} />
                Exportar Excel
              </button>
            </div>

            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full min-w-[1100px]">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Código</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Foto</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Data</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Dias em conserto</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                      {listaChegada === 'chegaram' ? 'Status' : 'Chegou'}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Data chegada</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Marca</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Modelo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Defeito</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Setor</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Qtd</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Valor Total</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {listaAtual.length === 0 ? (
                    <tr>
                      <td colSpan="15" className="px-4 py-8 text-center text-gray-500">
                        {listaChegada === 'chegaram'
                          ? `Nenhum equipamento chegou ainda em ${anoSelecionado}`
                          : `Nenhum equipamento em conserto em ${anoSelecionado}`}
                        {setorFiltro ? ` — ${setorFiltro}` : ''}
                      </td>
                    </tr>
                  ) : (
                    listaAtual.map((equipamento) => {
                      const dias = diasEmConserto(equipamento);
                      const diasClass =
                        equipamento.chegou
                          ? 'text-green-700 font-medium'
                          : dias == null
                            ? 'text-gray-500'
                            : dias >= 30
                              ? 'text-red-700 font-semibold'
                              : dias >= 15
                                ? 'text-amber-700 font-medium'
                                : 'text-gray-700';
                      return (
                      <tr key={equipamento.id} className={`hover:bg-gray-50 ${equipamento.chegou ? 'bg-green-50' : ''}`}>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">{equipamento.codigo_processo}</td>
                        <td className="px-4 py-3 text-sm">
                          {equipamento.tem_foto ? (
                            <button
                              type="button"
                              onClick={() =>
                                setFotoPopup({
                                  src: `${API_URL}/equipamentos/${equipamento.id}/foto?t=${equipamento.data_atualizacao || Date.now()}`,
                                  titulo: equipamento.codigo_processo,
                                })
                              }
                              className="block"
                              title="Ver foto"
                            >
                              <img
                                src={`${API_URL}/equipamentos/${equipamento.id}/foto?t=${equipamento.data_atualizacao || ''}`}
                                alt={`Foto ${equipamento.codigo_processo}`}
                                className="h-12 w-12 object-cover rounded border border-gray-300"
                              />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatDate(equipamento.data_solicitacao)}</td>
                        <td className={`px-4 py-3 text-sm whitespace-nowrap ${diasClass}`} title={equipamento.chegou ? 'Dias até a chegada' : 'Dias desde a solicitação'}>
                          {formatDiasConserto(dias)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {equipamento.chegou ? (
                            <div className="flex flex-col gap-1">
                              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded w-fit">
                                Chegou
                              </span>
                              <button
                                type="button"
                                onClick={() => handleToggleChegada(equipamento)}
                                className="text-[11px] text-gray-400 hover:text-gray-600 underline w-fit"
                                title="Reabrir com senha"
                              >
                                Reabrir
                              </button>
                            </div>
                          ) : (
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={() => handleToggleChegada(equipamento)}
                                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="text-xs font-medium text-gray-500">Marcar chegada</span>
                            </label>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {equipamento.chegou ? formatDate(equipamento.data_chegada) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{equipamento.descricao_equipamento}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{equipamento.marca_equipamento || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{equipamento.modelo_equipamento || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{equipamento.defeito_apresentado}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{equipamento.setor_origem}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{equipamento.quantidade} {equipamento.unidade}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-700">{formatCurrency(equipamento.valor_total)}</td>
                        <td className="px-4 py-3 text-sm">
                          {equipamento.chegou ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(equipamento)}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                title="Editar"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleDelete(equipamento.id)}
                                className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                title="Deletar"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {equipamento.chegou ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleGerarEmail(equipamento.id)}
                                className="p-1 text-purple-600 hover:bg-purple-100 rounded transition-colors"
                                title="Abrir no Outlook clássico"
                              >
                                <Mail size={18} />
                              </button>
                              <button
                                onClick={() => handleVerEmail(equipamento.id)}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                title="Ver Email (Outlook Web)"
                              >
                                <Eye size={18} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      </div>

      {fotoPopup && typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 2147483646, background: 'rgba(0,0,0,0.8)' }}
            onClick={() => setFotoPopup(null)}
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-bold text-gray-900">{fotoPopup.titulo}</h2>
                <button type="button" onClick={() => setFotoPopup(null)} className="p-1 rounded hover:bg-gray-100">
                  <X size={22} />
                </button>
              </div>
              <img src={fotoPopup.src} alt={fotoPopup.titulo} className="max-h-[75vh] w-full object-contain rounded" />
            </div>
          </div>,
          document.body
        )}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
              <h2 className="text-xl font-bold">
                {editingId
                  ? 'Editar Equipamento'
                  : formItens.length > 1
                    ? `Novo processo (${formItens.length} itens)`
                    : 'Novo Equipamento'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-white hover:bg-blue-700 rounded p-1 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {editingId ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código do Processo *</label>
                    <input
                      type="text"
                      name="codigo_processo"
                      value={formData.codigo_processo}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Solicitação *</label>
                    <input
                      type="date"
                      name="data_solicitacao"
                      value={formData.data_solicitacao}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição do Equipamento *</label>
                    <input
                      type="text"
                      name="descricao_equipamento"
                      value={formData.descricao_equipamento}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marca do Equipamento</label>
                    <input
                      type="text"
                      name="marca_equipamento"
                      value={formData.marca_equipamento}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modelo do Equipamento</label>
                    <input
                      type="text"
                      name="modelo_equipamento"
                      value={formData.modelo_equipamento}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Defeito Apresentado ou Ação *</label>
                    <input
                      type="text"
                      name="defeito_apresentado"
                      value={formData.defeito_apresentado}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Setor de Origem *</label>
                    <select
                      name="setor_origem"
                      value={formData.setor_origem}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selecione um setor...</option>
                      {setores.map((setor) => (
                        <option key={setor.id} value={setor.nome}>
                          {setor.nome} ({setor.codigo})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código do Setor</label>
                    <input
                      type="text"
                      name="codigo_setor"
                      value={formData.codigo_setor}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidade *</label>
                    <select
                      name="unidade"
                      value={formData.unidade}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="PC">PC</option>
                      <option value="PEÇA">PEÇA</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade *</label>
                    <input
                      type="number"
                      name="quantidade"
                      value={formData.quantidade}
                      onChange={handleChange}
                      required
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Unitário (R$)</label>
                    <input
                      type="number"
                      name="valor_unitario"
                      value={formData.valor_unitario}
                      onChange={handleChange}
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Total (R$)</label>
                    <input
                      type="text"
                      name="valor_total"
                      value={formData.valor_total}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Foto do equipamento</label>
                    <div className="flex flex-wrap items-start gap-3">
                      {formData.foto_preview ? (
                        <img src={formData.foto_preview} alt="Prévia" className="h-24 w-24 object-cover rounded-lg border" />
                      ) : null}
                      <div className="flex flex-col gap-2">
                        <label className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 w-fit">
                          <ImageIcon size={16} />
                          {formData.foto_preview ? 'Trocar foto' : 'Escolher foto'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              handleEditPhoto(e.target.files?.[0]);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        {formData.foto_preview ? (
                          <button type="button" onClick={handleRemoveEditPhoto} className="text-sm text-red-700 hover:underline text-left">
                            Remover foto
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Informações Complementares</label>
                    <textarea
                      name="informacoes_complementares"
                      value={formData.informacoes_complementares}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <p className="text-sm font-semibold text-blue-900 mb-3">
                      Dados comuns (valem para todos os itens)
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data de Solicitação *</label>
                        <input
                          type="date"
                          name="data_solicitacao"
                          value={formData.data_solicitacao}
                          onChange={handleChange}
                          required
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Setor de Origem *</label>
                        <select
                          name="setor_origem"
                          value={formData.setor_origem}
                          onChange={handleChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                          <option value="">Selecione um setor...</option>
                          {setores.map((setor) => (
                            <option key={setor.id} value={setor.nome}>
                              {setor.nome} ({setor.codigo})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Código do Setor</label>
                        <input
                          type="text"
                          name="codigo_setor"
                          value={formData.codigo_setor}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                          placeholder="Automático"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-blue-800 mt-3">
                      Códigos dos processos serão gerados em sequência a partir de{' '}
                      <strong>{formData.codigo_processo || '…'}</strong>
                      {formItens.length > 1 ? ` (${formItens.length} códigos)` : ''}.
                    </p>
                  </div>

                  {formItens.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-gray-200 p-4 bg-white shadow-sm"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-gray-800">
                          Item {index + 1}
                          {formData.codigo_processo ? (
                            <span className="ml-2 font-normal text-gray-500">
                              {(() => {
                                const m = String(formData.codigo_processo).match(/^C(\d+)\/(\d+)$/i);
                                if (!m) return '';
                                const n = parseInt(m[1], 10) + index;
                                return `→ C${String(n).padStart(3, '0')}/${m[2]}`;
                              })()}
                            </span>
                          ) : null}
                        </h3>
                        {formItens.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="px-2 py-1 text-sm text-red-700 hover:bg-red-50 rounded flex items-center gap-1"
                            title="Remover item"
                          >
                            <Minus size={16} />
                            Remover
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                          <input
                            type="text"
                            name="descricao_equipamento"
                            value={item.descricao_equipamento}
                            onChange={(e) => handleItemChange(index, e)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: MOTOR"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Defeito / Ação *</label>
                          <input
                            type="text"
                            name="defeito_apresentado"
                            value={item.defeito_apresentado}
                            onChange={(e) => handleItemChange(index, e)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: QUEIMADO"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                          <input
                            type="text"
                            name="marca_equipamento"
                            value={item.marca_equipamento}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: WEG"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                          <input
                            type="text"
                            name="modelo_equipamento"
                            value={item.modelo_equipamento}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: 50CV"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Unidade *</label>
                          <select
                            name="unidade"
                            value={item.unidade}
                            onChange={(e) => handleItemChange(index, e)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="PC">PC</option>
                            <option value="PEÇA">PEÇA</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade *</label>
                          <input
                            type="number"
                            name="quantidade"
                            value={item.quantidade}
                            onChange={(e) => handleItemChange(index, e)}
                            required
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Valor Unitário (R$)</label>
                          <input
                            type="number"
                            name="valor_unitario"
                            value={item.valor_unitario}
                            onChange={(e) => handleItemChange(index, e)}
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: 4000.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Valor Total (R$)</label>
                          <input
                            type="text"
                            name="valor_total"
                            value={item.valor_total}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Foto do equipamento</label>
                          <div className="flex flex-wrap items-start gap-3">
                            {item.foto_preview ? (
                              <img src={item.foto_preview} alt={`Prévia item ${index + 1}`} className="h-24 w-24 object-cover rounded-lg border" />
                            ) : null}
                            <div className="flex flex-col gap-2">
                              <label className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 w-fit">
                                <ImageIcon size={16} />
                                {item.foto_preview ? 'Trocar foto' : 'Escolher foto'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    handleItemPhoto(index, e.target.files?.[0]);
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                              {item.foto_preview ? (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveItemPhoto(index)}
                                  className="text-sm text-red-700 hover:underline text-left"
                                >
                                  Remover foto
                                </button>
                              ) : (
                                <p className="text-xs text-gray-500">Opcional. No celular: câmera ou galeria; no PC: arquivo.</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Informações Complementares</label>
                          <textarea
                            name="informacoes_complementares"
                            value={item.informacoes_complementares}
                            onChange={(e) => handleItemChange(index, e)}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Informações adicionais..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="w-full md:w-auto px-4 py-2.5 border-2 border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 font-semibold flex items-center justify-center gap-2"
                  >
                    <Plus size={18} />
                    Adicionar mais um item
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors flex items-center gap-2"
                >
                  <X size={18} />
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Save size={18} />
                  {editingId
                    ? 'Atualizar'
                    : formItens.length > 1
                      ? `Salvar ${formItens.length} itens`
                      : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-purple-600 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Mail size={24} />
                Texto para Email
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-white hover:bg-purple-700 rounded p-1 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <textarea
                value={emailText}
                readOnly
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono text-sm"
              />
              
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={handleCopyEmail}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? 'Copiado!' : 'Copiar Texto'}
                </button>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors flex items-center gap-2"
                >
                  <X size={18} />
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
